import React, { useState, useCallback } from 'react';
import {
  CheckCircle,
  XCircle,
  Filter,
  BrainCircuit,
  Hourglass,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { AiOutlineCheck, AiOutlineClose } from 'react-icons/ai';
import {
  FiAlertTriangle,
  FiMessageSquare,
  FiEdit2,
  FiLock,
  FiPaperclip,
  FiCheckCircle,
  FiX,
} from 'react-icons/fi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import toast from 'react-hot-toast';

import { appImages } from '../../utils/constants';
import { sanitizeHtml } from '../../utils/sanitize';
import { uploadFiles } from '../../services/api';
import SunEditorComponent from './SunEditorComponent';
import useMailPreview from '../../hooks/useMailPreview';
import useSharePointPlaceNames from '../../hooks/useSharePointPlaceNames';
import SharePointApprovalCard, {
  SHAREPOINT_EDITABLE_KEYS,
} from './SharePointApprovalCard';
import { SignaturePreview } from '../modals/MailSettingsContent';

// -- HtmlFieldPreview -------------------------------------------------
// Renders sanitised HTML (Confluence storage format, email body, etc.)
// inside a light-mode iframe blob — exactly like ToolApprovalUI does.
const HtmlFieldPreview = ({ html }) => {
  const iframeRef = React.useRef(null);
  const [src, setSrc] = React.useState('');

  React.useEffect(() => {
    const sanitized = sanitizeHtml(html);
    const fullHtml = `<!DOCTYPE html><html lang="en"><head><meta name="viewport" content="width=device-width,initial-scale=1.0"><style>body{margin:0;padding:12px 16px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:#d4d4d8;font-size:13px;line-height:1.6;background:#18181b;word-wrap:break-word;}*{box-sizing:border-box;max-width:100%;}table{width:100%!important;border-collapse:collapse;border-radius:6px;overflow:hidden;font-size:13px;}th,td{border:1px solid #3f3f46;padding:8px 12px;text-align:left;word-break:break-word;}th{background:#27272a;color:#a1a1aa;font-weight:600;}tr:nth-child(even){background:#1f1f23;}p{margin:0.4rem 0;}h1,h2,h3{color:#f4f4f5;margin:0.8rem 0 0.4rem;}code{background:#27272a;color:#a5b4fc;padding:2px 6px;border-radius:4px;font-size:12px;}pre{background:#27272a;padding:10px;border-radius:6px;overflow-x:auto;}a{color:#818cf8;}strong{color:#f4f4f5;}::-webkit-scrollbar{display:none;}body{-ms-overflow-style:none;scrollbar-width:none;}</style></head><body>${sanitized}</body></html>`;
    const blob = new Blob([fullHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [html]);

  const handleLoad = () => {
    try {
      const iframe = iframeRef.current;
      if (!iframe) return;
      const h = iframe.contentWindow.document.body.scrollHeight;
      iframe.style.height = `${Math.min(Math.max(h, 120), 500)}px`;
    } catch (_) {}
  };

  return (
    <iframe
      ref={iframeRef}
      src={src}
      title="Content preview"
      sandbox="allow-same-origin"
      scrolling="auto"
      className="mt-1 w-full rounded-lg border border-zinc-700/40 bg-[#18181b]"
      style={{ height: '120px', minHeight: '80px' }}
      onLoad={handleLoad}
    />
  );
};

// -- MarkdownFieldPreview ----------------------------------------------
// Renders standard Markdown using ReactMarkdown + remark-gfm.
// Uses the `components` prop for full Tailwind control — no CSS dependency.
const MD_REMARK = [remarkGfm];

const MD_COMPONENTS = {
  h1: ({ children }) => (
    <h1 className="mb-2 mt-3 text-base font-bold text-zinc-100 first:mt-0">
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="mb-1.5 mt-3 text-sm font-bold text-zinc-100 first:mt-0">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="mb-1 mt-2 text-sm font-semibold text-zinc-200 first:mt-0">
      {children}
    </h3>
  ),
  p: ({ children }) => (
    <p className="my-1 text-sm leading-relaxed text-zinc-300">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="my-1.5 ml-4 list-disc space-y-0.5 text-sm text-zinc-300">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="my-1.5 ml-4 list-decimal space-y-0.5 text-sm text-zinc-300">
      {children}
    </ol>
  ),
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => (
    <strong className="font-semibold text-zinc-100">{children}</strong>
  ),
  em: ({ children }) => <em className="italic text-zinc-400">{children}</em>,
  hr: () => <hr className="my-3 border-zinc-700" />,
  blockquote: ({ children }) => (
    <blockquote className="my-2 border-l-2 border-zinc-600 pl-3 text-sm text-zinc-400">
      {children}
    </blockquote>
  ),
  // Inline code vs fenced code block
  code: ({ inline, className, children }) => {
    if (inline) {
      return (
        <code className="rounded bg-zinc-800 px-1.5 py-0.5 font-mono text-xs text-violet-300">
          {children}
        </code>
      );
    }
    // fenced code block — strip language class for display
    const lang = (className || '').replace('language-', '') || 'code';
    return (
      <div className="my-2 overflow-hidden rounded-lg border border-zinc-700">
        {/* language badge */}
        <div className="border-b border-zinc-700 bg-zinc-800/80 px-3 py-1">
          <span className="font-mono text-[10px] text-zinc-500">{lang}</span>
        </div>
        <pre className="overflow-x-auto bg-zinc-900 p-3">
          <code className="font-mono text-xs leading-relaxed text-violet-200">
            {String(children).replace(/\n$/, '')}
          </code>
        </pre>
      </div>
    );
  },
  // Tables
  table: ({ children }) => (
    <div className="my-2 overflow-x-auto rounded-lg border border-zinc-700">
      <table className="w-full border-collapse text-xs">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-zinc-800">{children}</thead>,
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => (
    <tr className="border-b border-zinc-700/60 even:bg-zinc-800/30">
      {children}
    </tr>
  ),
  th: ({ children }) => (
    <th className="border-r border-zinc-700/60 px-3 py-2 text-left font-semibold text-zinc-400 last:border-r-0">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border-r border-zinc-700/60 px-3 py-2 text-zinc-300 last:border-r-0">
      {children}
    </td>
  ),
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-indigo-400 underline hover:text-indigo-300"
    >
      {children}
    </a>
  ),
};

const MarkdownFieldPreview = ({ content }) => {
  const text = String(content || '').trim();
  return (
    <div
      className="mt-1 overflow-y-auto rounded-lg border border-zinc-700/40 p-3"
      style={{ maxHeight: '320px' }}
    >
      <ReactMarkdown remarkPlugins={MD_REMARK} components={MD_COMPONENTS}>
        {text}
      </ReactMarkdown>
    </div>
  );
};

// -- RichTextPreview ---------------------------------------------------
// Fallback for Jira wiki markup or plain long text — shown raw.
const RichTextPreview = ({ content }) => {
  const text = String(content || '').trim();
  return (
    <div
      className="mt-1 overflow-y-auto rounded-lg border border-zinc-700/40"
      style={{ maxHeight: '300px' }}
    >
      <pre className="whitespace-pre-wrap break-words p-3 font-sans text-sm leading-relaxed text-zinc-300">
        {text}
      </pre>
    </div>
  );
};

// Detects Confluence storage format / generic HTML
const looksLikeHtml = (val) => {
  const s = String(val).trimStart();
  return (
    s.startsWith('<') ||
    s.includes('<ac:') ||
    s.includes('<p>') ||
    s.includes('<h1') ||
    s.includes('<table')
  );
};

// Detects Jira wiki markup (h2., {code}, {{monospace}}, etc.)
const looksLikeJiraWiki = (val) => {
  const s = String(val);
  return (
    !looksLikeHtml(s) &&
    (/^h[1-6]\.\s/m.test(s) ||
      /\{code(?::[^}]*)?\}/.test(s) ||
      /\{\{[^}]+\}\}/.test(s) ||
      /\{(info|note|warning|tip|quote|panel)\}/i.test(s))
  );
};

// Detects standard Markdown — only if NOT HTML or Jira wiki
const looksLikeMarkdown = (val) => {
  const s = String(val);
  if (looksLikeHtml(s) || looksLikeJiraWiki(s)) return false;
  return (
    s.length > 40 &&
    (/\*\*.+\*\*/.test(s) ||
      /^#{1,6}\s/m.test(s) ||
      /^[-*+]\s/m.test(s) ||
      /\[.+\]\(.+\)/.test(s) ||
      /`[^`]+`/.test(s) ||
      /^>\s/m.test(s) ||
      s.includes('\n\n'))
  );
};

// Detects opaque platform IDs (Graph message_id/task_id/folder_id, Jira
// account_id, etc.) — long, no whitespace, not a recognizable human label
// like a Jira issue key ("PROJ-123"). These carry zero information to a
// human even though they correctly identify the target record, so locked
// fields holding one get truncated instead of dumping the full base64-ish
// blob — the human-readable name is already in the card's description line.
const looksLikeOpaqueId = (val) => {
  const s = String(val);
  return s.length > 24 && !/\s/.test(s);
};

// "AAMkADRhMWU4NzZm...HQyLAAA=" instead of the full 100+ char blob.
const truncateId = (val, head = 10, tail = 6) => {
  const s = String(val);
  if (s.length <= head + tail + 1) return s;
  return `${s.slice(0, head)}…${s.slice(-tail)}`;
};

// -- editable field extraction (shared by read-only view and edit mode) --
const FIELD_LABEL_MAP = {
  project_identifier: 'Project Identifier',
  issue_id_or_key: 'Issue ID/Key',
  transition_id: 'Transition ID',
  transition_name: 'Transition Status',
  base_url: 'Base URL',
  repository_name: 'Repository Name',
  branch_name: 'Branch Name',
  pull_number: 'Pull Request #',
};

const GENERIC_SKIP_KEYS = new Set([
  'action_description',
  'fields_to_update',
  'additional_fields',
  'payload',
]);

// Fields with zero human value — raw opaque IDs (accountIds etc.) a user
// can't meaningfully read or hand-edit. If the AI resolved the wrong
// person, that's corrected via feedback, not by retyping an accountId.
const HIDDEN_KEYS = new Set(['watcher_ids']);

// Fields that identify WHICH existing record the action targets, not what
// to change about it. Editing these doesn't modify content — it silently
// redirects the whole call to a different issue/sprint/board/etc — so they
// stay visible (the user should see what's being acted on) but not editable.
const LOCKED_KEYS = new Set([
  'action',
  'issue_key',
  'sprint_id',
  'board_id',
  'comment_id',
  'epic_key',
  'link_id',
  'transition_id',
  'project_identifier',
  // Confluence
  'page_id',
  'space_id',
  'space_key',
  'account_id',
  // Outlook
  'message_id',
  'event_id',
  'contact_id',
  'folder_id',
  'task_id',
  // Microsoft To Do
  'list_id',
  'checklist_item_id',
  // Microsoft Teams
  'team_id',
  'channel_id',
  'chat_id',
  'reply_to_message_id',
]);

// jira_manage_issue actions that target an issue which already exists (has
// a real issue_key at this point) -- attaching a file makes sense for any
// of these. Excludes 'create' (no issue_key yet), 'delete' (about to
// remove the issue, attaching first is contradictory), and 'attach_file'
// itself (that's a direct AI-initiated attach with its own full-page
// upload flow already, not something this mini toggle needs to duplicate).
const ATTACHABLE_JIRA_ACTIONS = new Set([
  'get',
  'update',
  'transition',
  'set_estimate',
  'add_watcher',
]);

/**
 * Walks the same source locations the card's field list uses to display,
 * but returns {label, path, value} so edits can be written back to the
 * exact spot they were read from — path is an array of keys into
 * parsedArguments, e.g. ['payload', 'additional_fields', 'description'].
 */
const collectEditableFields = (parsedArguments) => {
  const payload = parsedArguments.payload || {};
  // Structure varies by tool:
  //   jira_manage_issue create → payload.{ summary, project_identifier, additional_fields.{ description, priority, … } }
  //   jira_manage_issue update → payload.{ issue_id_or_key, fields_to_update.{ description, … } }
  //   confluence_manage_pages  → payload.{ title, space_id, content }
  const sources = [
    {
      base: parsedArguments,
      prefix: ['additional_fields'],
      obj: parsedArguments.additional_fields || {},
    },
    {
      base: parsedArguments,
      prefix: ['fields_to_update'],
      obj: parsedArguments.fields_to_update || {},
    },
    {
      base: parsedArguments,
      prefix: ['payload', 'additional_fields'],
      obj: payload.additional_fields || {},
    },
    {
      base: parsedArguments,
      prefix: ['payload', 'fields_to_update'],
      obj: payload.fields_to_update || {},
    },
    { base: parsedArguments, prefix: ['payload'], obj: payload },
    { base: parsedArguments, prefix: [], obj: parsedArguments },
  ];

  // Pre-pass: pick up optional "<key>_display_name" companions. This is a
  // generic, tool-agnostic convention -- any tool schema can offer an
  // AI-fillable "X_display_name" alongside an opaque ID/object field X
  // (e.g. assignee_display_name next to assignee), purely so the approval
  // card can show a real name instead of an accountId. It's always safe to
  // add: the compact-tool routers only ever pull out specific known keys
  // (fields_to_update, additional_fields, mentions, …) from payload, so an
  // extra sibling key the AI includes is never forwarded to the real API
  // call -- it can only ever affect this rendering, nothing else.
  const displayNameOverrides = new Map();
  [...sources].reverse().forEach(({ obj }) => {
    Object.entries(obj).forEach(([key, value]) => {
      if (
        key.endsWith('_display_name') &&
        typeof value === 'string' &&
        value.trim()
      ) {
        displayNameOverrides.set(
          key.slice(0, -'_display_name'.length),
          value.trim(),
        );
      }
    });
  });

  const byLabel = new Map();

  // Iterate deepest-first, shallower (more specific) sources overwrite
  [...sources].reverse().forEach(({ prefix, obj }) => {
    Object.entries(obj).forEach(([key, value]) => {
      // Rendered via its base field's row (see displayNameOverrides above),
      // never as a row of its own.
      if (key.endsWith('_display_name')) return;
      if (
        GENERIC_SKIP_KEYS.has(key) ||
        HIDDEN_KEYS.has(key) ||
        key.startsWith('customfield_')
      )
        return;
      // undefined means this key just isn't present in this particular
      // source object (normal, most sources don't define most keys) -- not
      // meaningful, skip silently. An explicit null is different: it's a
      // deliberate "clear this field" instruction and a real part of what's
      // about to be applied, so it still gets a row rather than vanishing.
      if (value === undefined) return;
      const label =
        FIELD_LABEL_MAP[key] ||
        key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
      const path = [...prefix, key];
      const locked = LOCKED_KEYS.has(key);
      const displayNameOverride = displayNameOverrides.get(key);

      if (value === null) {
        byLabel.set(label, {
          label,
          path,
          value: '(cleared)',
          readOnly: true,
        });
        return;
      }

      if (Array.isArray(value)) {
        // Arrays of primitives (issue keys, mentions-by-id, labels, …) are
        // editable as a comma-separated list. Arrays of objects (e.g.
        // mentions: [{account_id, display_name}]) are still surfaced so
        // they're not silently hidden from the approver, but as read-only
        // JSON — editing structured list items inline isn't supported.
        const isPrimitiveArray = value.every(
          (item) => item === null || typeof item !== 'object',
        );
        if (isPrimitiveArray) {
          byLabel.set(label, {
            label,
            path,
            value: value.join(', '),
            isArray: true,
            locked,
          });
        } else {
          byLabel.set(label, {
            label,
            path,
            value: JSON.stringify(value, null, 2),
            readOnly: true,
          });
        }
        return;
      }

      if (typeof value === 'object') {
        // A single object value (e.g. assignee: {accountId: "..."}) was
        // previously dropped here entirely -- silently invisible on the
        // approval card even though it's a real part of what's about to be
        // applied. Surface it the same way an array of objects already is:
        // read-only JSON, not editable inline, but never invisible -- unless
        // a displayNameOverride resolved a human-readable name for it, in
        // which case show that instead, with the raw JSON as a tooltip.
        const rawJson = JSON.stringify(value, null, 2);
        byLabel.set(label, {
          label,
          path,
          value: displayNameOverride || rawJson,
          readOnly: true,
          rawValue: displayNameOverride ? rawJson : undefined,
        });
        return;
      }
      byLabel.set(label, {
        label,
        path,
        value: displayNameOverride || String(value),
        locked,
        rawValue: displayNameOverride ? String(value) : undefined,
      });
    });
  });

  return Array.from(byLabel.values());
};

// Common Microsoft Graph / Outlook Windows time zone names (start_time,
// end_time, time_zone are interpreted literally in this zone by Graph — no
// UTC conversion). Not exhaustive (Graph accepts ~130), but covers the
// common cases; an unrecognized existing value still shows via the
// "custom…" fallback option so it's never silently lost.
const OUTLOOK_TIME_ZONES = [
  'India Standard Time',
  'UTC',
  'GMT Standard Time',
  'Central European Standard Time',
  'Eastern Standard Time',
  'Central Standard Time',
  'Mountain Standard Time',
  'Pacific Standard Time',
  'China Standard Time',
  'Tokyo Standard Time',
  'Singapore Standard Time',
  'AUS Eastern Standard Time',
  'W. Europe Standard Time',
  'Russian Standard Time',
  'Arabian Standard Time',
];

const setIn = (obj, path, value) => {
  const clone = structuredClone(obj);
  let cursor = clone;
  for (let i = 0; i < path.length - 1; i++) {
    cursor = cursor[path[i]];
  }
  cursor[path[path.length - 1]] = value;
  return clone;
};

// -- ChipListInput --------------------------------------------------------
// Editor for comma-separated array fields (labels, etc.) — chips you can
// remove individually, plus a small text box to add new ones (Enter or
// comma commits). Keeps the same comma-joined string as its value so it
// stays a drop-in replacement for the plain text input handleApprove
// already knows how to split back into an array.
const ChipListInput = ({ value, onChange, inputRef, placeholder }) => {
  const chips = value
    .split(',')
    .map((c) => c.trim())
    .filter(Boolean);
  const [draft, setDraft] = useState('');

  const commitDraft = () => {
    const next = draft.trim().replace(/,$/, '');
    setDraft('');
    if (!next || chips.includes(next)) return;
    onChange([...chips, next].join(', '));
  };

  const removeChip = (chip) => {
    onChange(chips.filter((c) => c !== chip).join(', '));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commitDraft();
    } else if (e.key === 'Backspace' && !draft && chips.length > 0) {
      removeChip(chips[chips.length - 1]);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-[#5652CF]/70 bg-zinc-800/60 px-2 py-1.5 focus-within:ring-1 focus-within:ring-[#5652CF]/40">
      {chips.map((chip) => (
        <span
          key={chip}
          className="flex items-center gap-1 rounded-md bg-zinc-700/70 px-2 py-1 text-xs font-medium text-zinc-200"
        >
          {chip}
          <button
            type="button"
            onClick={() => removeChip(chip)}
            title={`Remove ${chip}`}
            className="text-zinc-400 transition-colors hover:text-zinc-100"
          >
            <AiOutlineClose className="h-2.5 w-2.5" />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={commitDraft}
        placeholder={chips.length === 0 ? placeholder || 'Add…' : ''}
        className="min-w-[90px] flex-1 bg-transparent px-1 py-0.5 text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none"
      />
    </div>
  );
};

// -- FieldRow -------------------------------------------------------------
// Read-only by default with a pencil icon; clicking it swaps that single
// field in place for an input/textarea. No global edit mode — each field
// toggles independently.
const FieldRow = ({
  label,
  path,
  value,
  editedValue,
  isEditing,
  onToggleEdit,
  onChange,
  isArray,
  readOnly,
  locked,
  rawValue,
}) => {
  const fieldKey = path.join('.');
  const current = editedValue ?? value;
  const isTouched = current !== value;
  const isLongForm =
    readOnly || isArray || value.length > 60 || value.includes('\n');
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (isEditing) inputRef.current?.focus();
  }, [isEditing]);

  // Outlook calendar's start_time/end_time/time_zone get native pickers —
  // Graph interprets these literally with no UTC conversion, so a raw text
  // field inviting typos (wrong ISO format, misspelled zone name) is a real
  // footgun here in a way it isn't for most other free-text fields.
  const isDateTimeField = fieldKey === 'start_time' || fieldKey === 'end_time';
  const isTimeZoneField = fieldKey === 'time_zone';
  // Jira's due date is a bare date (no time component) — a native date
  // picker beats a free-text field the same way it does for Outlook's
  // datetime fields above (no format-typo risk, no guessing what "next
  // Friday" was resolved to before you approve it).
  const isDueDateField = fieldKey === 'duedate' || fieldKey === 'due_date';

  if (isEditing) {
    const fieldClassName =
      'w-full rounded-lg border border-[#5652CF]/70 bg-zinc-800/60 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-600 transition-colors focus:outline-none focus:ring-1 focus:ring-[#5652CF]/40';
    return (
      <div>
        <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-zinc-500">
          {label}
          {isArray && (
            <span className="ml-1.5 normal-case text-zinc-600">
              (Enter or comma to add, Backspace to remove)
            </span>
          )}
        </label>
        <div className="flex items-start gap-2">
          {isDateTimeField ? (
            <input
              ref={inputRef}
              type="datetime-local"
              step="1"
              className={fieldClassName}
              value={current}
              onChange={(e) => onChange(fieldKey, e.target.value)}
            />
          ) : isDueDateField ? (
            <input
              ref={inputRef}
              type="date"
              className={fieldClassName}
              value={current}
              onChange={(e) => onChange(fieldKey, e.target.value)}
            />
          ) : isTimeZoneField ? (
            <select
              ref={inputRef}
              className={fieldClassName}
              value={current}
              onChange={(e) => onChange(fieldKey, e.target.value)}
            >
              {!OUTLOOK_TIME_ZONES.includes(current) && (
                <option value={current}>{current} (custom)</option>
              )}
              {OUTLOOK_TIME_ZONES.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
          ) : isArray ? (
            <ChipListInput
              inputRef={inputRef}
              value={current}
              onChange={(v) => onChange(fieldKey, v)}
            />
          ) : isLongForm ? (
            <textarea
              ref={inputRef}
              rows={12}
              className={`${fieldClassName} min-h-[240px] resize-y`}
              value={current}
              onChange={(e) => onChange(fieldKey, e.target.value)}
            />
          ) : (
            <input
              ref={inputRef}
              type="text"
              className={fieldClassName}
              value={current}
              onChange={(e) => onChange(fieldKey, e.target.value)}
            />
          )}
          <button
            onClick={() => onToggleEdit(fieldKey)}
            title="Done editing"
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-[#5652CF]/15 text-[#8481e0] transition-colors hover:bg-[#5652CF]/25"
          >
            <AiOutlineCheck className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="group flex items-start justify-between gap-2">
      <div className="min-w-0 flex-1">
        <span className="mb-1 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-zinc-500">
          {label}
          {isTouched && <span className="h-1 w-1 rounded-full bg-[#8481e0]" />}
        </span>
        {rawValue ? (
          // A "<field>_display_name" companion resolved a human-readable
          // name for what would otherwise be an opaque ID/object -- show
          // that instead, with the raw value available on hover for anyone
          // who needs to verify the exact ID being used.
          <p
            title={rawValue}
            className="cursor-help break-words text-sm font-medium text-zinc-200"
          >
            {current}
          </p>
        ) : readOnly ? (
          <pre className="whitespace-pre-wrap break-words rounded-md bg-black/30 p-2 font-mono text-xs text-zinc-400">
            {current}
          </pre>
        ) : locked && looksLikeOpaqueId(current) ? (
          <p
            title={current}
            className="cursor-help break-words font-mono text-xs text-zinc-500"
          >
            {truncateId(current)}
          </p>
        ) : looksLikeHtml(current) ? (
          <HtmlFieldPreview html={current} />
        ) : looksLikeMarkdown(current) ? (
          <MarkdownFieldPreview content={current} />
        ) : looksLikeJiraWiki(current) ? (
          <RichTextPreview content={current} />
        ) : (
          <p className="break-words text-sm font-medium text-zinc-200">
            {current}
          </p>
        )}
      </div>
      {readOnly ? null : locked ? (
        <span
          title={`${label} identifies the target of this action and can't be edited — use Feedback to point at a different one`}
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center text-zinc-600"
        >
          <FiLock className="h-3 w-3" />
        </span>
      ) : (
        <button
          onClick={() => onToggleEdit(fieldKey)}
          title={`Edit ${label}`}
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-zinc-500 opacity-0 transition-opacity hover:bg-zinc-800 hover:text-zinc-200 focus:opacity-100 group-hover:opacity-100"
        >
          <FiEdit2 className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
};

// -- FieldList --------------------------------------------------------
const FieldList = ({
  fields,
  editedValues,
  editingFieldKeys,
  onToggleEdit,
  onChange,
}) => {
  if (fields.length === 0) return null;
  return (
    <div className="space-y-2">
      {fields.map((field) => {
        const fieldKey = field.path.join('.');
        return (
          <FieldRow
            key={fieldKey}
            {...field}
            editedValue={editedValues[fieldKey]}
            isEditing={editingFieldKeys.has(fieldKey)}
            onToggleEdit={onToggleEdit}
            onChange={onChange}
          />
        );
      })}
    </div>
  );
};

// -- Mail compose fields (email-editor-style UI for outlook_manage_mail
// write actions, reusing the recipient-chip pattern from
// BriefingReplyEmail/BriefingForwardEmail but wired into the approval
// card's existing edit/approve flow instead of a standalone send button) --

const EmailChipInput = ({ label, emails, onChange, placeholder }) => {
  const [inputValue, setInputValue] = useState('');
  const emailPattern = /^[a-zA-Z0-9._%+-]+@([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/;

  const addEmail = () => {
    const value = inputValue.trim().replace(/,$/, '');
    if (!value) return;
    if (!emailPattern.test(value)) return;
    if (!emails.includes(value)) onChange([...emails, value]);
    setInputValue('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      addEmail();
    } else if (e.key === 'Backspace' && !inputValue && emails.length > 0) {
      onChange(emails.slice(0, -1));
    }
  };

  const removeEmail = (email) => onChange(emails.filter((e) => e !== email));

  return (
    <div className="flex items-start gap-2">
      <span className="mt-2.5 w-9 flex-shrink-0 text-[11px] font-medium uppercase tracking-wide text-zinc-500">
        {label}
      </span>
      <div className="flex min-h-[36px] w-full flex-wrap items-center gap-1 rounded-md border border-zinc-700/50 bg-zinc-900/40 p-1.5 text-xs text-zinc-300">
        {emails.map((email) => (
          <span
            key={email}
            className="inline-flex items-center rounded bg-zinc-700/50 px-2 py-1 text-zinc-200"
          >
            {email}
            <button
              onClick={() => removeEmail(email)}
              className="ml-2 text-zinc-400 hover:text-red-400"
            >
              &times;
            </button>
          </span>
        ))}
        <input
          type="text"
          className="min-w-[100px] flex-grow bg-transparent text-zinc-300 outline-none"
          placeholder={emails.length === 0 ? placeholder : ''}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={addEmail}
        />
      </div>
    </div>
  );
};

// Which fields outlook_manage_mail's compose-style actions expose, and how
// the body should be edited — rich HTML for send/create_draft/reply, plain
// text for forward's short "comment" (forward_outlook_email doesn't rewrite
// the original message body, it just prepends a short note).
const MAIL_ACTION_FIELDS = {
  send: {
    to: 'recipients',
    showSubject: true,
    bodyKey: 'body',
    bodyMode: 'rich',
  },
  create_draft: {
    to: 'recipients',
    showSubject: true,
    bodyKey: 'body',
    bodyMode: 'rich',
  },
  forward: {
    to: 'recipients',
    showSubject: false,
    bodyKey: 'comment',
    bodyMode: 'plain',
  },
  reply: {
    to: 'override_to_recipients',
    showSubject: false,
    bodyKey: 'body',
    bodyMode: 'rich',
  },
};

// Current value of a mail field: the user's edit if any, else the model's.
// Arrays live in editedValues as comma-joined strings (see handleApprove).
const readMailArray = (key, parsedArguments, editedValues) => {
  if (key in editedValues) {
    const raw = editedValues[key];
    return raw
      ? raw
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
  }
  return Array.isArray(parsedArguments[key]) ? parsedArguments[key] : [];
};

const readMailString = (key, parsedArguments, editedValues) =>
  key in editedValues ? editedValues[key] : parsedArguments[key] || '';

/** Recipients + body of the mail as the card currently shows it. */
const currentMailDraft = (toolAction, parsedArguments, editedValues) => {
  const config = MAIL_ACTION_FIELDS[toolAction];
  if (!config) return { recipients: [], body: '' };
  return {
    recipients: [config.to, 'cc_recipients', 'bcc_recipients'].flatMap((key) =>
      readMailArray(key, parsedArguments, editedValues),
    ),
    body: readMailString(config.bodyKey, parsedArguments, editedValues),
  };
};

const AUDIENCE_BADGES = {
  internal: {
    label: 'Internal',
    className: 'bg-emerald-900/30 text-emerald-300 border-emerald-800/50',
  },
  external: {
    label: 'External',
    className: 'bg-sky-900/30 text-sky-300 border-sky-800/50',
  },
  mixed: {
    label: 'External · includes colleagues',
    className: 'bg-sky-900/30 text-sky-300 border-sky-800/50',
  },
};
const RELATION_LABELS = { manager: 'to your manager', team: 'to your team' };

/** Audience badge + internal-content warnings for one drafted mail. */
const MailAudienceStrip = ({ preview }) => {
  const badge = preview && AUDIENCE_BADGES[preview.audience];
  const warnings = preview?.warnings || [];
  if (!badge && warnings.length === 0) return null;
  return (
    <div className="space-y-1.5">
      {badge && (
        <div className="flex items-center gap-2 text-[11px]">
          <span
            className={`rounded-full border px-2 py-0.5 font-medium ${badge.className}`}
          >
            {badge.label}
          </span>
          {RELATION_LABELS[preview.relation] && (
            <span className="text-zinc-500">
              {RELATION_LABELS[preview.relation]}
            </span>
          )}
        </div>
      )}
      {warnings.length > 0 && (
        <div className="rounded-md border border-amber-800/50 bg-amber-900/20 px-2.5 py-1.5 text-xs text-amber-300">
          <p className="flex items-center gap-2 font-medium">
            <FiAlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
            This mail goes outside the company.
          </p>
          <ul className="ml-5 mt-0.5 list-disc">
            {warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

/**
 * The model already wrote a name after the closing line ("Warm regards,
 * The Team") and the user's signature will be added below it: offer to drop
 * the name so the mail isn't signed twice.
 */
const SignoffNameNotice = ({ preview, onRemove }) =>
  preview?.signoff_name && preview?.body_without_signoff ? (
    <div className="flex items-center justify-between gap-2 rounded-md border border-zinc-700/60 bg-zinc-800/40 px-2.5 py-1.5 text-xs text-zinc-300">
      <span>
        The body already ends with &ldquo;{preview.signoff_name}&rdquo; and your
        signature is added below it.
      </span>
      <button
        type="button"
        onClick={() => onRemove(preview.body_without_signoff)}
        className="flex-shrink-0 rounded border border-zinc-600 px-2 py-0.5 text-[11px] text-zinc-200 hover:bg-zinc-700"
      >
        Remove it
      </button>
    </div>
  ) : null;

/** The signature the backend appends after approval, with a per-mail opt-out. */
const MailSignatureRow = ({ html, include, onIncludeChange }) =>
  html ? (
    <div>
      <label className="mb-1 flex cursor-pointer items-center gap-2 text-[11px] font-medium uppercase tracking-wide text-zinc-500">
        <input
          type="checkbox"
          className="h-3.5 w-3.5 accent-[#5652CF]"
          checked={include}
          onChange={(e) => onIncludeChange(e.target.checked)}
        />
        Signature
      </label>
      <SignaturePreview
        html={html}
        className={include ? '' : 'line-through opacity-40'}
      />
    </div>
  ) : null;

const MailComposeFields = ({
  toolAction,
  parsedArguments,
  editedValues,
  onChange,
  preview,
  includeSignature,
  onIncludeSignatureChange,
}) => {
  // Pushed into the rich editor when the body is changed for the user
  // (e.g. "Remove it" on a duplicate sign-off name).
  const [replacedBody, setReplacedBody] = useState(undefined);
  const config = MAIL_ACTION_FIELDS[toolAction];
  if (!config) return null;

  const replaceBody = (html) => {
    setReplacedBody(html);
    onChange(config.bodyKey, html);
  };

  const getArrayValue = (key) =>
    readMailArray(key, parsedArguments, editedValues);
  const getStringValue = (key) =>
    readMailString(key, parsedArguments, editedValues);
  const impliedReplyTo =
    toolAction === 'reply' &&
    (parsedArguments.reply_all || !getArrayValue(config.to).length)
      ? preview?.reply_to || []
      : [];

  return (
    <div className="space-y-3 rounded-xl border border-zinc-800/60 bg-zinc-900/30 p-4">
      <MailAudienceStrip preview={preview} />
      {impliedReplyTo.length > 0 ? (
        // The model named nobody: the reply goes where Outlook sends it.
        <div className="flex items-start gap-2">
          <span className="mt-1.5 w-9 flex-shrink-0 text-[11px] font-medium uppercase tracking-wide text-zinc-500">
            To
          </span>
          <div className="flex flex-wrap items-center gap-1 text-xs">
            {impliedReplyTo.map((email) => (
              <span
                key={email}
                className="inline-flex items-center rounded bg-zinc-700/50 px-2 py-1 text-zinc-200"
              >
                {email}
              </span>
            ))}
            <span className="text-[11px] text-zinc-500">
              {parsedArguments.reply_all
                ? 'everyone on the original mail'
                : 'the original sender'}
            </span>
          </div>
        </div>
      ) : (
        <EmailChipInput
          label="To"
          emails={getArrayValue(config.to)}
          onChange={(emails) => onChange(config.to, emails.join(', '))}
          placeholder="Recipients..."
        />
      )}
      <EmailChipInput
        label="Cc"
        emails={getArrayValue('cc_recipients')}
        onChange={(emails) => onChange('cc_recipients', emails.join(', '))}
        placeholder="Cc..."
      />
      <EmailChipInput
        label="Bcc"
        emails={getArrayValue('bcc_recipients')}
        onChange={(emails) => onChange('bcc_recipients', emails.join(', '))}
        placeholder="Bcc..."
      />
      {config.showSubject && (
        <div className="flex items-center gap-2">
          <span className="w-9 flex-shrink-0 text-[11px] font-medium uppercase tracking-wide text-zinc-500">
            Subj
          </span>
          <input
            type="text"
            className="min-h-[36px] w-full rounded-md border border-zinc-700/50 bg-zinc-900/40 px-2 py-1.5 text-sm text-zinc-200 outline-none focus:border-[#5652CF]"
            value={getStringValue('subject')}
            onChange={(e) => onChange('subject', e.target.value)}
          />
        </div>
      )}
      <div>
        <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-zinc-500">
          {config.bodyMode === 'rich' ? 'Body' : 'Comment'}
        </span>
        {config.bodyMode === 'rich' ? (
          <div className="overflow-hidden rounded-md border border-zinc-700/50">
            <SunEditorComponent
              value={getStringValue(config.bodyKey)}
              onChange={(html) => onChange(config.bodyKey, html)}
              justUpdatedValue={replacedBody}
            />
          </div>
        ) : (
          <textarea
            rows={3}
            className="w-full resize-none rounded-md border border-zinc-700/50 bg-zinc-900/40 p-2 text-sm text-zinc-200 outline-none focus:border-[#5652CF]"
            value={getStringValue(config.bodyKey)}
            onChange={(e) => onChange(config.bodyKey, e.target.value)}
          />
        )}
      </div>
      {includeSignature && (
        <SignoffNameNotice preview={preview} onRemove={replaceBody} />
      )}
      {/* Appended by the backend after approval -- the user's own signature,
          found automatically (Settings > Mail). Read-only here. */}
      <MailSignatureRow
        html={preview?.signature_html}
        include={includeSignature}
        onIncludeChange={onIncludeSignatureChange}
      />
    </div>
  );
};

/**
 * outlook_manage_mail action='send_batch': several personalised mails in one
 * approval. Each can be left out or edited; only the kept ones are sent.
 */
const MailBatchFields = ({ messages, batchState, onBatchChange }) => (
  <div className="space-y-2">
    <p className="text-xs text-zinc-500">
      {messages.length} separate emails. Untick any you don&apos;t want sent;
      your signature is added to each.
    </p>
    {messages.map((message, index) => {
      const state = batchState[index] || {};
      const kept = state.kept !== false;
      const subject = state.subject ?? message.subject ?? '';
      const body = state.body ?? message.body ?? '';
      return (
        <div
          // eslint-disable-next-line react/no-array-index-key -- messages have no id and never reorder
          key={index}
          className={`rounded-xl border border-zinc-800/60 bg-zinc-900/30 p-3 ${kept ? '' : 'opacity-50'}`}
        >
          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              className="mt-0.5 h-3.5 w-3.5 accent-[#5652CF]"
              aria-label={`Send email ${index + 1}`}
              checked={kept}
              onChange={(e) => onBatchChange(index, { kept: e.target.checked })}
            />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-xs text-zinc-400">
                To: {(message.recipients || []).join(', ')}
              </span>
              <input
                type="text"
                disabled={!kept}
                aria-label={`Subject of email ${index + 1}`}
                className="mt-1 w-full rounded-md border border-zinc-700/50 bg-zinc-900/40 px-2 py-1 text-sm text-zinc-200 outline-none focus:border-[#5652CF]"
                value={subject}
                onChange={(e) =>
                  onBatchChange(index, { subject: e.target.value })
                }
              />
            </span>
          </div>
          {kept && (
            <details className="mt-2">
              <summary className="cursor-pointer text-[11px] font-medium uppercase tracking-wide text-zinc-500">
                Body
              </summary>
              <div className="mt-1 overflow-hidden rounded-md border border-zinc-700/50">
                <SunEditorComponent
                  value={body}
                  onChange={(html) => onBatchChange(index, { body: html })}
                />
              </div>
            </details>
          )}
        </div>
      );
    })}
  </div>
);

// -- Tool → service mapping (used to pick the right app icon + label) --
// Covers every tool name that can appear in an approval card.
const TOOL_SERVICE = [
  // Memory (save/forget always need approval -- see utils/constants.js)
  { prefix: 'save_memory', service: 'Memory', icon: () => null },
  { prefix: 'forget_memory', service: 'Memory', icon: () => null },
  // Jira
  { prefix: 'jira', service: 'Jira', icon: () => appImages.jira },
  { prefix: 'create_jira', service: 'Jira', icon: () => appImages.jira },
  { prefix: 'update_jira', service: 'Jira', icon: () => appImages.jira },
  {
    prefix: 'delete_issue_in_jira',
    service: 'Jira',
    icon: () => appImages.jira,
  },
  { prefix: 'add_comment_to', service: 'Jira', icon: () => appImages.jira },
  {
    prefix: 'update_comment_on_jira',
    service: 'Jira',
    icon: () => appImages.jira,
  },
  { prefix: 'transition_status', service: 'Jira', icon: () => appImages.jira },
  { prefix: 'move_issues', service: 'Jira', icon: () => appImages.jira },
  {
    prefix: 'move_specific_issues',
    service: 'Jira',
    icon: () => appImages.jira,
  },
  { prefix: 'add_watchers', service: 'Jira', icon: () => appImages.jira },
  { prefix: 'add_attachment_to', service: 'Jira', icon: () => appImages.jira },
  { prefix: 'swap_jira', service: 'Jira', icon: () => appImages.jira },
  { prefix: 'remove_issues', service: 'Jira', icon: () => appImages.jira },
  {
    prefix: 'update_the_estimation',
    service: 'Jira',
    icon: () => appImages.jira,
  },
  // Confluence
  {
    prefix: 'confluence',
    service: 'Confluence',
    icon: () => appImages.confluence,
  },
  {
    prefix: 'create_page_confluence',
    service: 'Confluence',
    icon: () => appImages.confluence,
  },
  {
    prefix: 'update_page_confluence',
    service: 'Confluence',
    icon: () => appImages.confluence,
  },
  {
    prefix: 'delete_page_confluence',
    service: 'Confluence',
    icon: () => appImages.confluence,
  },
  {
    prefix: 'create_space_confluence',
    service: 'Confluence',
    icon: () => appImages.confluence,
  },
  {
    prefix: 'delete_space_confluence',
    service: 'Confluence',
    icon: () => appImages.confluence,
  },
  // Gmail / Outlook
  { prefix: 'send_email', service: 'Gmail', icon: () => appImages.gmail },
  {
    prefix: 'create_draft_on_gmail',
    service: 'Gmail',
    icon: () => appImages.gmail,
  },
  {
    prefix: 'reply_to_email_on_gmail',
    service: 'Gmail',
    icon: () => appImages.gmail,
  },
  {
    prefix: 'send_message_in_thread',
    service: 'Gmail',
    icon: () => appImages.gmail,
  },
  {
    prefix: 'outlook_manage',
    service: 'Outlook',
    icon: () => appImages.microsoft_outlook,
  },
  {
    prefix: 'todo_manage',
    service: 'Microsoft To Do',
    icon: () => appImages.microsoft_todo,
  },
  {
    prefix: 'teams_manage',
    service: 'Microsoft Teams',
    icon: () => appImages.microsoft_teams,
  },
  {
    prefix: 'teams_search_people',
    service: 'Microsoft Teams',
    icon: () => appImages.microsoft_teams,
  },
  // SharePoint (same icon as its Integrations tile)
  {
    prefix: 'sharepoint_',
    service: 'SharePoint',
    icon: () => appImages.microsoft_onedrive,
  },
  {
    prefix: 'send_email_outlook',
    service: 'Outlook',
    icon: () => appImages.microsoft_outlook,
  },
  {
    prefix: 'reply_to_outlook',
    service: 'Outlook',
    icon: () => appImages.microsoft_outlook,
  },
  {
    prefix: 'create_draft_email_outlook',
    service: 'Outlook',
    icon: () => appImages.microsoft_outlook,
  },
  {
    prefix: 'forward_outlook',
    service: 'Outlook',
    icon: () => appImages.microsoft_outlook,
  },
  {
    prefix: 'delete_email',
    service: 'Outlook',
    icon: () => appImages.microsoft_outlook,
  },
  // Calendar
  {
    prefix: 'create_google_calendar',
    service: 'Google Calendar',
    icon: () => appImages.google_calendar,
  },
  {
    prefix: 'create_calendar_event_outlook',
    service: 'Outlook Calendar',
    icon: () => appImages.microsoft_outlook,
  },
  {
    prefix: 'update_calendar',
    service: 'Calendar',
    icon: () => appImages.google_calendar,
  },
  {
    prefix: 'delete_event_outlook',
    service: 'Outlook Calendar',
    icon: () => appImages.microsoft_outlook,
  },
  // GitHub
  { prefix: 'create_github', service: 'GitHub', icon: () => appImages.github },
  { prefix: 'update_github', service: 'GitHub', icon: () => appImages.github },
  {
    prefix: 'merge_pull_request',
    service: 'GitHub',
    icon: () => appImages.github,
  },
  {
    prefix: 'create_pull_request',
    service: 'GitHub',
    icon: () => appImages.github,
  },
  { prefix: 'create_branch', service: 'GitHub', icon: () => appImages.github },
  // Teams / Slack
  {
    prefix: 'send_direct_message',
    service: 'Teams',
    icon: () => appImages.microsoft_teams,
  },
  {
    prefix: 'send_message_in_channel',
    service: 'Teams',
    icon: () => appImages.microsoft_teams,
  },
  {
    prefix: 'create_team_microsoft',
    service: 'Teams',
    icon: () => appImages.microsoft_teams,
  },
];

/**
 * Given a raw tool function name, returns { service, icon } for the header.
 * Falls back to a generic label derived from the tool name itself.
 */
const getToolService = (toolName) => {
  for (const entry of TOOL_SERVICE) {
    if (toolName.startsWith(entry.prefix) || toolName === entry.prefix) {
      return { service: entry.service, icon: entry.icon() };
    }
  }
  // Fallback: capitalise the first word as service name, no icon
  const firstWord = toolName.split('_')[0];
  return {
    service: firstWord.charAt(0).toUpperCase() + firstWord.slice(1),
    icon: null,
  };
};

/**
 * Build a human-readable title from a tool name + optional action.
 * e.g. "jira_manage_issue" + "create" → "Create Jira Issue"
 */
const getCardTitle = (toolName, action) => {
  const titleMap = {
    // Jira issue
    'jira_manage_issue:create': 'Create Jira Issue',
    'jira_manage_issue:update': 'Update Jira Issue',
    'jira_manage_issue:delete': 'Delete Jira Issue',
    'jira_manage_issue:transition': 'Transition Issue Status',
    'jira_manage_issue:set_estimate': 'Set Issue Estimate',
    'jira_manage_issue:add_watcher': 'Add Watcher to Issue',
    'jira_manage_issue:attach_file': 'Attach File to Issue',
    // Jira comments
    'jira_manage_comments:add': 'Add Comment',
    'jira_manage_comments:update': 'Update Comment',
    'jira_manage_comments:delete': 'Delete Comment',
    // Jira sprints
    'jira_manage_sprints:create': 'Create Sprint',
    'jira_manage_sprints:update': 'Update Sprint',
    'jira_manage_sprints:swap': 'Swap Sprints',
    // Jira epics / move / links
    'jira_manage_epics:move': 'Move Issues to Epic',
    'jira_manage_epics:remove': 'Remove Issues from Epic',
    'jira_manage_epics:rank': 'Rank Epics',
    'jira_manage_links:create': 'Create Issue Link',
    'jira_manage_links:delete': 'Delete Issue Link',
    'jira_move_issues:to_sprint': 'Move Issues to Sprint',
    'jira_move_issues:to_backlog': 'Move Issues to Backlog',
    'jira_move_issues:to_board': 'Move Issues to Board',
    'jira_move_issues:rank': 'Rank Issues',
    // Confluence pages
    'confluence_manage_pages:create': 'Create Confluence Page',
    'confluence_manage_pages:update': 'Update Confluence Page',
    'confluence_manage_pages:delete': 'Delete Confluence Page',
    // Confluence spaces
    'confluence_manage_spaces:create': 'Create Confluence Space',
    'confluence_manage_spaces:delete': 'Delete Confluence Space',
    // Outlook mail
    'outlook_manage_mail:send': 'Send Outlook Email',
    'outlook_manage_mail:create_draft': 'Create Outlook Draft',
    'outlook_manage_mail:update': 'Update Outlook Email',
    'outlook_manage_mail:delete': 'Delete Outlook Email',
    'outlook_manage_mail:forward': 'Forward Outlook Email',
    'outlook_manage_mail:reply': 'Reply to Outlook Email',
    'outlook_manage_mail:download_attachments': 'Download Email Attachments',
    'outlook_manage_mail:send_draft': 'Send Outlook Draft',
    'outlook_manage_mail:send_batch': 'Send Personalised Emails',
    // Outlook calendar
    'outlook_manage_calendar:create': 'Create Outlook Event',
    'outlook_manage_calendar:update': 'Update Outlook Event',
    'outlook_manage_calendar:delete': 'Delete Outlook Event',
    // Outlook contacts
    'outlook_manage_contacts:create': 'Create Outlook Contact',
    'outlook_manage_contacts:update': 'Update Outlook Contact',
    'outlook_manage_contacts:delete': 'Delete Outlook Contact',
    // Outlook mail folders
    'outlook_manage_folders:create': 'Create Mail Folder',
    'outlook_manage_folders:delete': 'Delete Mail Folder',
    // Outlook tasks
    'outlook_manage_tasks:create': 'Create Outlook Task',
    'outlook_manage_tasks:update': 'Update Outlook Task',
    'outlook_manage_tasks:delete': 'Delete Outlook Task',
    // Microsoft To Do lists
    'todo_manage_lists:create': 'Create To Do List',
    'todo_manage_lists:delete': 'Delete To Do List',
    // Microsoft To Do tasks
    'todo_manage_tasks:create': 'Create To Do Task',
    'todo_manage_tasks:update': 'Update To Do Task',
    'todo_manage_tasks:delete': 'Delete To Do Task',
    'todo_manage_tasks:complete': 'Complete To Do Task',
    'todo_manage_tasks:undo_complete': 'Un-complete To Do Task',
    // Microsoft To Do checklist
    'todo_manage_checklist:add': 'Add Checklist Item',
    'todo_manage_checklist:set_checked': 'Update Checklist Item',
    'todo_manage_checklist:delete': 'Delete Checklist Item',
    // Microsoft Teams channels (create/delete currently disabled — see teamsToolsGPT.py)
    'teams_manage_channels:create': 'Create Teams Channel',
    'teams_manage_channels:delete': 'Delete Teams Channel',
    // Microsoft Teams channel messages
    'teams_manage_channel_messages:send': 'Send Teams Message',
    'teams_manage_channel_messages:reply': 'Reply to Teams Message',
    // Microsoft Teams chats (currently disabled — see teamsToolsGPT.py)
    'teams_manage_chats:send': 'Send Teams Chat Message',
    'teams_manage_chats:send_direct': 'Send Direct Teams Message',
    // SharePoint
    'sharepoint_manage_items:create_folder': 'Create Folder',
    'sharepoint_manage_items:create_word': 'Create Word Document',
    'sharepoint_manage_items:create_excel': 'Create Excel Workbook',
    'sharepoint_manage_items:create_powerpoint': 'Create PowerPoint',
    'sharepoint_manage_items:update_word': 'Edit Word Document',
    'sharepoint_manage_items:rename': 'Rename',
    'sharepoint_manage_items:move': 'Move',
    'sharepoint_manage_items:copy': 'Copy',
    'sharepoint_manage_items:delete': 'Delete',
    'sharepoint_manage_items:restore_version': 'Restore Earlier Version',
    'sharepoint_excel:update_range': 'Write Excel Cells',
    'sharepoint_excel:add_sheet': 'Add Excel Sheet',
    'sharepoint_excel:add_table_rows': 'Add Excel Table Rows',
    'sharepoint_sharing:share_with_people': 'Share File',
    'sharepoint_sharing:create_link': 'Create Sharing Link',
    'sharepoint_sharing:remove_access': 'Stop Sharing',
    'sharepoint_onenote:create_page': 'Create OneNote Page',
    // Memory
    save_memory: 'Remember This',
    forget_memory: 'Forget From Memory',
  };
  const key = action ? `${toolName}:${action}` : toolName;
  if (titleMap[key]) return titleMap[key];
  // Fallback: prettify the tool name
  return toolName.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

// -- MiniAttachToggle -------------------------------------------------------
// A small "Attach file" affordance for a card whose action targets an
// already-existing issue (see ATTACHABLE_JIRA_ACTIONS -- 'create' doesn't
// have a real issue_key yet, so there's nothing to attach to). Click
// toggles a compact inline picker open; picking a file uploads it immediately and
// fires the real attach_file call directly via onAttachFile, independent of
// this card's own approve/decline -- attaching a file isn't a proposed
// change the user reviews, it's an action they're choosing to take right
// now. Reports success/failure, then folds itself back to the idle toggle
// once the outcome has actually been visible for a moment.
const MiniAttachToggle = ({ issueKey, taskId, onAttachFile }) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | uploading | done | error
  const [fileName, setFileName] = useState('');
  const inputRef = React.useRef(null);

  const reset = () => {
    setOpen(false);
    setStatus('idle');
    setFileName('');
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !onAttachFile) return;
    setFileName(file.name);
    setStatus('uploading');
    try {
      const accessToken = localStorage.getItem('projectToken');
      if (!accessToken) {
        throw new Error('Not authenticated. Please log in again.');
      }
      const uploadResult = await uploadFiles(accessToken, taskId || '', [file]);
      const s3Filename = uploadResult?.files_info?.[0]?.s3_filename;
      if (!s3Filename) {
        throw new Error(uploadResult?.error || 'File upload failed.');
      }
      const result = await onAttachFile(issueKey, s3Filename);
      if (result === false || result?.is_error || result?.error) {
        throw new Error(
          (result && result.error) || 'Failed to attach the file.',
        );
      }
      setStatus('done');
      toast.success(`Attached "${file.name}" to ${issueKey}`);
      setTimeout(reset, 1600);
    } catch (err) {
      setStatus('error');
      toast.error(
        typeof err === 'string' ? err : err?.message || 'Upload failed.',
      );
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={`Attach a file to ${issueKey}`}
        className="flex h-9 items-center gap-1.5 rounded-lg border border-zinc-700 px-3 text-xs font-medium text-zinc-400 transition-colors hover:border-zinc-600 hover:bg-zinc-800 hover:text-zinc-200"
      >
        <FiPaperclip className="h-3.5 w-3.5" />
        Attach file
      </button>
    );
  }

  return (
    <div className="flex h-9 items-center gap-2 rounded-lg border border-zinc-700 bg-zinc-800/60 px-3">
      {status === 'uploading' ? (
        <>
          <span className="h-3.5 w-3.5 flex-shrink-0 animate-spin rounded-full border-2 border-zinc-600 border-t-zinc-200" />
          <span className="max-w-[140px] truncate text-xs text-zinc-300">
            Uploading {fileName}…
          </span>
        </>
      ) : status === 'done' ? (
        <>
          <FiCheckCircle className="h-3.5 w-3.5 flex-shrink-0 text-emerald-400" />
          <span className="max-w-[140px] truncate text-xs text-emerald-300">
            Attached {fileName}
          </span>
        </>
      ) : status === 'error' ? (
        <>
          <FiAlertTriangle className="h-3.5 w-3.5 flex-shrink-0 text-red-400" />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="text-xs font-medium text-red-300 hover:text-red-200"
          >
            Failed — try again
          </button>
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            onChange={handleFileChange}
          />
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="flex items-center gap-1.5 text-xs font-medium text-zinc-200 hover:text-white"
          >
            <FiPaperclip className="h-3.5 w-3.5" />
            Choose file…
          </button>
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            onChange={handleFileChange}
          />
        </>
      )}
      <button
        type="button"
        onClick={reset}
        title="Cancel"
        disabled={status === 'uploading'}
        className="ml-1 flex-shrink-0 text-zinc-500 transition-colors hover:text-zinc-300 disabled:opacity-40"
      >
        <FiX className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

// -- Inline Approval Card -----------------------------------------------

const InlineApprovalCard = ({
  toolCall,
  onApprove,
  onDecline,
  onAttachFile,
  taskId,
}) => {
  const [feedbackText, setFeedbackText] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);
  const feedbackInputRef = React.useRef(null);
  const [includeSignature, setIncludeSignature] = useState(true);
  // send_batch: index -> { kept, subject, body } overrides.
  const [batchState, setBatchState] = useState({});
  const declineReasonId = React.useId();

  React.useEffect(() => {
    if (showFeedback) feedbackInputRef.current?.focus();
  }, [showFeedback]);
  // fieldKey (path.join('.')) -> live edited string value, only set once a
  // field has actually been switched into edit mode and changed.
  const [editedValues, setEditedValues] = useState({});
  // Which fields are currently showing as an input rather than read-only text.
  const [editingFieldKeys, setEditingFieldKeys] = useState(() => new Set());

  const handleToggleFieldEdit = useCallback((fieldKey) => {
    setEditingFieldKeys((prev) => {
      const next = new Set(prev);
      if (next.has(fieldKey)) next.delete(fieldKey);
      else next.add(fieldKey);
      return next;
    });
  }, []);

  const parsedArguments = React.useMemo(() => {
    try {
      return JSON.parse(toolCall.function?.arguments || '{}');
    } catch {
      return {};
    }
  }, [toolCall.function?.arguments]);

  const actionDesc = parsedArguments.action_description;
  const toolName = toolCall.function?.name || '';
  const toolAction = parsedArguments.action || null;
  // Attaching a file needs a real, already-existing issue to attach to --
  // 'create' doesn't have an issue_key yet at this point; any action that
  // does target an existing issue (see ATTACHABLE_JIRA_ACTIONS) can offer it.
  const attachableIssueKey =
    toolName === 'jira_manage_issue' &&
    ATTACHABLE_JIRA_ACTIONS.has(toolAction) &&
    typeof parsedArguments.issue_key === 'string' &&
    parsedArguments.issue_key.trim()
      ? parsedArguments.issue_key.trim()
      : null;
  const allFields = React.useMemo(
    () => collectEditableFields(parsedArguments),
    [parsedArguments],
  );
  // Locked identity fields (action, project_identifier, page_id, …) are
  // short and never edited — stacking each as its own full-width labeled
  // block wastes a lot of vertical space for a batch review flow. Pull them
  // into one compact meta strip instead; only the fields someone might
  // actually want to read closely or edit get the full FieldRow treatment.
  // SharePoint cards describe the change in names (SharePointApprovalCard);
  // its ids (place_id, item_id, folder_id, …) mean nothing to a person, so
  // no id strip, and only the content stays editable.
  const isSharePointTool = toolName.startsWith('sharepoint_');
  const sharePointPlaceNames = useSharePointPlaceNames(isSharePointTool);
  const lockedFields = isSharePointTool
    ? []
    : allFields.filter((f) => f.locked);
  const editableFields = isSharePointTool
    ? allFields.filter(
        (f) => f.path.length === 1 && SHAREPOINT_EDITABLE_KEYS.has(f.path[0]),
      )
    : allFields.filter((f) => !f.locked);

  // Resolve service icon + label
  const { service, icon } = getToolService(toolName);
  const cardTitle = getCardTitle(toolName, toolAction);
  const subtitleAction = toolAction ? ` · ${toolAction}` : '';

  const handleFieldChange = useCallback((fieldKey, value) => {
    setEditedValues((prev) => ({ ...prev, [fieldKey]: value }));
  }, []);

  const hasEdits = editableFields.some(
    ({ path, value }) => (editedValues[path.join('.')] ?? value) !== value,
  );

  const isMailComposeAction =
    toolName === 'outlook_manage_mail' && toolAction in MAIL_ACTION_FIELDS;
  const isMailBatchAction =
    toolName === 'outlook_manage_mail' &&
    toolAction === 'send_batch' &&
    Array.isArray(parsedArguments.messages);

  // Audience, signature and warnings for the mail as currently edited.
  const mailDraft = isMailComposeAction
    ? currentMailDraft(toolAction, parsedArguments, editedValues)
    : null;
  const mailPreview = useMailPreview({
    recipients: mailDraft?.recipients,
    body: mailDraft?.body,
    replyToMessageId:
      toolAction === 'reply' ? parsedArguments.message_id : undefined,
    replyAll: toolAction === 'reply' && Boolean(parsedArguments.reply_all),
    attachmentCount: Array.isArray(parsedArguments.file_paths_for_attachments)
      ? parsedArguments.file_paths_for_attachments.length
      : 0,
    enabled: isMailComposeAction,
  });
  const optOutOfSignature =
    isMailComposeAction &&
    Boolean(mailPreview?.signature_html) &&
    !includeSignature;

  const batchMessages = React.useMemo(
    () => (isMailBatchAction ? parsedArguments.messages : []),
    [isMailBatchAction, parsedArguments],
  );
  const batchEdited = Object.keys(batchState).length > 0;
  const batchKeptCount = batchMessages.filter(
    (_, index) => batchState[index]?.kept !== false,
  ).length;
  const handleBatchChange = useCallback((index, change) => {
    setBatchState((prev) => ({
      ...prev,
      [index]: { ...prev[index], ...change },
    }));
  }, []);

  const handleApprove = useCallback(() => {
    if (!hasEdits && !optOutOfSignature && !batchEdited) {
      onApprove(toolCall.id, toolCall);
      return;
    }
    let finalArguments = optOutOfSignature
      ? { ...parsedArguments, include_signature: false }
      : parsedArguments;
    if (batchEdited) {
      finalArguments = {
        ...finalArguments,
        messages: batchMessages
          .map((message, index) => ({ message, edit: batchState[index] || {} }))
          .filter(({ edit }) => edit.kept !== false)
          .map(({ message, edit }) => ({
            ...message,
            ...(edit.subject !== undefined && { subject: edit.subject }),
            ...(edit.body !== undefined && { body: edit.body }),
          })),
      };
    }
    editableFields.forEach(({ path, isArray, readOnly }) => {
      const fieldKey = path.join('.');
      if (readOnly || !(fieldKey in editedValues)) return;
      const raw = editedValues[fieldKey];
      const finalValue = isArray
        ? raw
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean)
        : raw;
      finalArguments = setIn(finalArguments, path, finalValue);
    });
    const updatedToolCall = {
      ...toolCall,
      function: {
        ...toolCall.function,
        arguments: JSON.stringify(finalArguments),
      },
      // The model's draft, so the backend can learn from what the user changed.
      ...(hasEdits && { originalArguments: toolCall.function.arguments }),
    };
    onApprove(toolCall.id, updatedToolCall);
  }, [
    toolCall,
    onApprove,
    hasEdits,
    optOutOfSignature,
    batchEdited,
    batchMessages,
    batchState,
    parsedArguments,
    editableFields,
    editedValues,
  ]);

  const handleDecline = useCallback(() => {
    onDecline(toolCall.id, 'Declined by user');
  }, [toolCall.id, onDecline]);

  const handleFeedbackSubmit = useCallback(() => {
    if (!feedbackText.trim()) return;
    onDecline(toolCall.id, feedbackText.trim());
    setShowFeedback(false);
    setFeedbackText('');
  }, [feedbackText, toolCall.id, onDecline]);

  // Delete warning covers both old atomic AND compact action='delete'.
  // Confluence page delete only moves to trash (Confluence has no single-call
  // permanent delete for a live page — see delete_page_confluence) — that's
  // recoverable, so it gets a softer, accurate banner instead of the
  // "cannot be undone" one used for genuinely permanent deletes.
  const isRecoverableDeleteTool =
    toolName === 'confluence_manage_pages' && toolAction === 'delete';
  const isPermanentDeleteTool =
    toolName === 'delete_issue_in_jira' ||
    toolName === 'delete_task_from_microsoft_todo' ||
    toolName === 'delete_task_list_from_microsoft_todo' ||
    ([
      'jira_manage_issue',
      'jira_manage_comments',
      'jira_manage_sprints',
      'jira_manage_links',
      'confluence_manage_spaces',
      // Outlook delete (mail/event/contact/folder/task) — verified live
      // against Graph API that a deleted message does not land in Deleted
      // Items on this tenant, and this app has no restore tool regardless,
      // so all outlook_manage_* delete actions get the permanent warning.
      'outlook_manage_mail',
      'outlook_manage_calendar',
      'outlook_manage_contacts',
      'outlook_manage_folders',
      'outlook_manage_tasks',
      // Microsoft To Do delete actions — list delete removes the list AND
      // every task in it, task/checklist-item delete has no restore path.
      'todo_manage_lists',
      'todo_manage_tasks',
      'todo_manage_checklist',
      // Microsoft Teams channel delete is permanent with no restore path.
      // Currently disabled in the backend tool schema (missing Channel.Delete.All
      // scope — see teamsToolsGPT.py) but listed here so it's correct immediately
      // once that action is re-enabled.
      'teams_manage_channels',
    ].includes(toolName) &&
      toolAction === 'delete');

  return (
    // No card shell — renders flat inside ActionTrackerEmbedded's container
    <>
      {/* Branded tool header strip */}
      <div className="flex items-center gap-3 border-b border-zinc-800/60 bg-zinc-800/30 px-4 py-2.5">
        {/* App icon badge */}
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-zinc-700/80 p-1.5">
          {icon ? (
            <img
              src={icon}
              alt={service}
              className="h-full w-full object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          ) : (
            <span className="text-[10px] font-bold text-zinc-400">
              {service.slice(0, 2).toUpperCase()}
            </span>
          )}
        </div>
        {/* Title + subtitle */}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-zinc-100">{cardTitle}</p>
          <p className="truncate text-xs text-zinc-500">
            {service}
            {subtitleAction}
          </p>
        </div>
        {hasEdits && (
          <span className="flex-shrink-0 rounded-full bg-[#5652CF]/15 px-2.5 py-1 text-[11px] font-medium text-[#8481e0]">
            Edited
          </span>
        )}
      </div>

      {/* Content + Actions */}
      <div className="space-y-2.5 px-4 pb-3.5 pt-3">
        {/* Locked identity fields (action, project/space/page id, …) — short,
            never editable, so one compact strip instead of a full labeled
            block each. */}
        {lockedFields.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-zinc-800/60 pb-2.5">
            {lockedFields.map((f) => {
              const display = looksLikeOpaqueId(f.value)
                ? truncateId(f.value)
                : f.value;
              return (
                <span
                  key={f.path.join('.')}
                  title={f.value}
                  className="text-xs text-zinc-500"
                >
                  <span className="uppercase tracking-wide text-zinc-600">
                    {f.label}
                  </span>{' '}
                  <span className="font-medium text-zinc-400">{display}</span>
                </span>
              );
            })}
          </div>
        )}
        {/* Content area */}
        {isRecoverableDeleteTool ? (
          <div className="flex items-center gap-2.5 rounded-xl border border-amber-800/50 bg-amber-900/20 p-4 text-sm font-semibold text-amber-400">
            <FiAlertTriangle className="h-4 w-4 flex-shrink-0" />
            This moves the page to Confluence's trash — it's recoverable, not a
            permanent delete.
          </div>
        ) : isPermanentDeleteTool ? (
          <div className="flex items-center gap-2.5 rounded-xl border border-red-800/50 bg-red-900/20 p-4 text-sm font-semibold text-red-400">
            <FiAlertTriangle className="h-4 w-4 flex-shrink-0" />
            Are you sure you want to permanently delete this item? This cannot
            be undone.
          </div>
        ) : isMailComposeAction ? (
          <div className="space-y-2">
            {actionDesc && (
              <p className="text-sm leading-relaxed text-zinc-400">
                {actionDesc}
              </p>
            )}
            <MailComposeFields
              toolAction={toolAction}
              parsedArguments={parsedArguments}
              editedValues={editedValues}
              onChange={handleFieldChange}
              preview={mailPreview}
              includeSignature={includeSignature}
              onIncludeSignatureChange={setIncludeSignature}
            />
          </div>
        ) : isSharePointTool ? (
          <div className="space-y-2.5">
            <SharePointApprovalCard
              toolName={toolName}
              action={toolAction}
              args={parsedArguments}
              placeNames={sharePointPlaceNames}
            />
            <FieldList
              fields={editableFields}
              editedValues={editedValues}
              editingFieldKeys={editingFieldKeys}
              onToggleEdit={handleToggleFieldEdit}
              onChange={handleFieldChange}
            />
          </div>
        ) : isMailBatchAction ? (
          <div className="space-y-2">
            {actionDesc && (
              <p className="text-sm leading-relaxed text-zinc-400">
                {actionDesc}
              </p>
            )}
            <MailBatchFields
              messages={batchMessages}
              batchState={batchState}
              onBatchChange={handleBatchChange}
            />
          </div>
        ) : (
          <div className="space-y-2">
            {actionDesc && (
              <p className="text-sm leading-relaxed text-zinc-400">
                {actionDesc}
              </p>
            )}
            <FieldList
              fields={editableFields}
              editedValues={editedValues}
              editingFieldKeys={editingFieldKeys}
              onToggleEdit={handleToggleFieldEdit}
              onChange={handleFieldChange}
            />
          </div>
        )}

        {/* Feedback panel — a clearly separated, labeled area rather than a
            bare textarea squeezed between the fields and the buttons. */}
        {showFeedback && (
          <div className="rounded-xl border border-amber-800/40 bg-amber-900/10 p-3">
            <label
              htmlFor={declineReasonId}
              className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-amber-400/90"
            >
              Reason for declining
            </label>
            <textarea
              id={declineReasonId}
              ref={feedbackInputRef}
              rows={4}
              placeholder="Tell the AI what to change, or why this shouldn't happen..."
              className="w-full resize-y rounded-lg border border-zinc-700 bg-zinc-800/60 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-600 focus:border-[#5652CF] focus:outline-none"
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
            />
          </div>
        )}

        {/* Action buttons. Attach (when this targets an existing issue)
            sits on the opposite side from Feedback/Decline/Approve -- it's
            an independent action the user can take right now, not part of
            the approve/decline decision, so it reads as visually separate
            rather than folded into that group. Decline keeps a visible
            border at rest (not just on hover) so its weight as a distinct,
            destructive-leaning action reads at a glance, and Approve stays
            the one filled/primary button. */}
        <div
          className={`flex items-center gap-2 border-t border-zinc-800/60 pt-2.5 ${
            attachableIssueKey && !showFeedback
              ? 'justify-between'
              : 'justify-end'
          }`}
        >
          {attachableIssueKey && !showFeedback && (
            <MiniAttachToggle
              issueKey={attachableIssueKey}
              taskId={taskId}
              onAttachFile={onAttachFile}
            />
          )}
          {/* Feedback/Decline/Approve (or Cancel/Send Feedback) are wrapped
              in their own group so `justify-between` on the row above only
              ever splits it into two things -- Attach vs. this whole
              cluster -- instead of spacing all four buttons out evenly as
              flat siblings. */}
          <div className="flex items-center gap-2">
            {showFeedback ? (
              <>
                <button
                  onClick={() => setShowFeedback(false)}
                  className="flex h-10 min-w-[104px] items-center justify-center rounded-lg border border-zinc-700 px-4 text-sm font-medium text-zinc-400 transition-colors hover:border-zinc-600 hover:text-zinc-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleFeedbackSubmit}
                  disabled={!feedbackText.trim()}
                  className="flex h-10 min-w-[104px] items-center justify-center gap-1.5 rounded-lg bg-[#5652CF] px-4 text-sm font-semibold text-white transition-colors hover:bg-[#4845b0] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FiMessageSquare className="h-4 w-4" />
                  Send Feedback
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setShowFeedback(true)}
                  title="Ask the AI to reconsider this action, instead of editing it yourself"
                  className="flex h-10 min-w-[104px] items-center justify-center gap-1.5 rounded-lg border border-transparent px-3.5 text-sm font-medium text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-200"
                >
                  <FiMessageSquare className="h-3.5 w-3.5" />
                  Feedback
                </button>
                <button
                  onClick={handleDecline}
                  className="flex h-10 min-w-[104px] items-center justify-center gap-1.5 rounded-lg border border-red-900/50 px-3.5 text-sm font-medium text-red-400/90 transition-colors hover:border-red-800 hover:bg-red-900/20 hover:text-red-300"
                >
                  <AiOutlineClose className="h-3.5 w-3.5" />
                  Decline
                </button>
                <button
                  onClick={handleApprove}
                  disabled={isMailBatchAction && batchKeptCount === 0}
                  className="flex h-10 min-w-[112px] items-center justify-center gap-2 rounded-lg bg-[#5652CF] px-4 text-sm font-semibold text-white shadow-sm shadow-[#5652CF]/30 transition-colors hover:bg-[#4845b0] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <AiOutlineCheck className="h-4 w-4" />
                  {isMailBatchAction
                    ? `Send ${batchKeptCount} of ${batchMessages.length}`
                    : hasEdits || batchEdited
                      ? 'Approve Edited'
                      : 'Approve'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// -- ArgumentsDisplay ---------------------------------------------

// Applies the same HIDDEN_KEYS / opaque-ID-truncation rules the pre-approval
// field editor uses (see collectEditableFields above), so this raw JSON view
// doesn't show a user things the approval step deliberately hid from them —
// e.g. watcher_ids, or a raw 100+ char Graph message_id. "<field>_display_name"
// companions are UI hints already folded into the structured card above this
// raw view (or the tool's own row) — showing them again here as their own
// key would just be noise, so they're dropped the same way HIDDEN_KEYS are.
const sanitizeArgsForDisplay = (value) => {
  if (Array.isArray(value)) {
    return value.map(sanitizeArgsForDisplay);
  }
  if (value && typeof value === 'object') {
    const result = {};
    for (const [key, val] of Object.entries(value)) {
      if (HIDDEN_KEYS.has(key) || key.endsWith('_display_name')) continue;
      if (typeof val === 'string' && looksLikeOpaqueId(val)) {
        result[key] = truncateId(val);
      } else {
        result[key] = sanitizeArgsForDisplay(val);
      }
    }
    return result;
  }
  return value;
};

const ArgumentsDisplay = ({ args, args_streaming }) => {
  if ((!args || Object.keys(args).length === 0) && !args_streaming) {
    return (
      <div className="py-2 pl-11 pr-4 text-xs italic text-zinc-500">
        No arguments provided.
      </div>
    );
  }

  return (
    <div className="pb-3 pl-11 pr-4 pt-1">
      <div className="custom-scrollbar max-h-[460px] overflow-y-auto rounded-md bg-black/30 p-3">
        <pre className="whitespace-pre-wrap break-words font-mono text-xs text-zinc-400">
          <code>
            {args && Object.keys(args).length > 0
              ? JSON.stringify(sanitizeArgsForDisplay(args), null, 2)
              : args_streaming}
          </code>
        </pre>
      </div>
    </div>
  );
};

// -- Main Component -----------------------------------------------

const ActionTrackerEmbedded = ({
  currentToolsStatus = [],
  toolCallApprovalList = [],
  isCurrent = false,
  onApprove,
  onDecline,
  onAttachFile,
  taskId,
}) => {
  const [expandedIndex, setExpandedIndex] = useState(null);
  // Which pending item the user has manually navigated to via the </>
  // arrows, if any. Tracked by id (not a plain index) so it survives the
  // pending list shrinking/reordering as other items get resolved —
  // falls back to the first still-pending item once this one resolves or
  // was never set.
  const [viewedApprovalId, setViewedApprovalId] = useState(null);

  // Build a map: tool id → full toolCall object from approval list
  const approvalItemMap = React.useMemo(() => {
    const map = new Map();
    toolCallApprovalList.forEach((tc) => map.set(tc.id, tc));
    return map;
  }, [toolCallApprovalList]);

  const actions = React.useMemo(() => {
    const approvalMap = new Map(
      toolCallApprovalList.map((approval) => [
        approval.id,
        {
          approved: approval.approved,
          approvalNeeded: approval.approvalNeeded,
          disapproved: approval.disapproved,
        },
      ]),
    );
    return currentToolsStatus
      .map((toolCall) => {
        let argumentsParsed = {};
        let gotErrorWhileParsing = false;
        try {
          argumentsParsed = JSON.parse(toolCall.function?.arguments || '{}');
        } catch (e) {
          gotErrorWhileParsing = true;
        }

        const argsToDisplay = { ...argumentsParsed };
        delete argsToDisplay.action_description;

        return {
          id: toolCall.id,
          status: toolCall.status,
          running_phase: toolCall.running_phase,
          approvalNeeded: approvalMap.get(toolCall.id)
            ? approvalMap.get(toolCall.id).approvalNeeded &&
              !approvalMap.get(toolCall.id).approved
            : false,
          // Set synchronously by handleDisapprove (DataProvider.js) the
          // instant the user declines/gives feedback on THIS item — well
          // before the backend confirms and toolCall.status below catches
          // up to "failed". Read separately from `status` so the row can
          // reflect it immediately instead of showing the generic "still
          // running" spinner for an item the user has already responded to.
          disapprovedByUser: approvalMap.get(toolCall.id)
            ? !!approvalMap.get(toolCall.id).disapproved
            : false,
          action_description:
            argumentsParsed.action_description || 'Processing Action',
          tool_name: toolCall.function?.name,
          args: argsToDisplay,
          args_streaming: gotErrorWhileParsing
            ? toolCall.function?.arguments
            : null,
        };
      })
      .filter((action) => action.tool_name !== 'record_thought_process');
  }, [currentToolsStatus, toolCallApprovalList]);

  const handleToggle = (index) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
  };

  if (actions.length === 0) return null;

  // Show pending approvals one at a time, in order, instead of stacking a
  // full InlineApprovalCard under every row at once -- with 10 parallel
  // tool calls that used to mean 10 full cards' worth of vertical space.
  // toolCallApprovalList entries persist for the whole batch (handleApprove/
  // handleDisapprove in DataProvider.js only ever flip approved/disapproved
  // flags on the existing entry, never remove it -- see git history), so
  // totalInBatch is stable across the whole flow.
  const batchActions = actions.filter((action) =>
    approvalItemMap.has(action.id),
  );
  const totalInBatch = batchActions.length;
  const pendingApprovalActions = batchActions.filter(
    (action) => action.approvalNeeded,
  );
  // Prefer whatever the user manually navigated to (via the </> arrows), as
  // long as it's still pending; otherwise default to the first pending item.
  const activeApproval =
    pendingApprovalActions.find((a) => a.id === viewedApprovalId) ||
    pendingApprovalActions[0] ||
    null;
  const activePendingIndex = activeApproval
    ? pendingApprovalActions.findIndex((a) => a.id === activeApproval.id)
    : -1;
  const activeBatchPosition = activeApproval
    ? batchActions.findIndex((a) => a.id === activeApproval.id) + 1
    : 0;
  const canGoPrevApproval = activePendingIndex > 0;
  const canGoNextApproval =
    activePendingIndex >= 0 &&
    activePendingIndex < pendingApprovalActions.length - 1;
  const goToPrevApproval = () => {
    if (canGoPrevApproval) {
      setViewedApprovalId(pendingApprovalActions[activePendingIndex - 1].id);
    }
  };
  const goToNextApproval = () => {
    if (canGoNextApproval) {
      setViewedApprovalId(pendingApprovalActions[activePendingIndex + 1].id);
    }
  };
  // While anything is still pending, only show the row for the item
  // currently under review — not every row in the batch at once. Once
  // everything is resolved, fall back to the full list as a final summary.
  const visibleActions =
    activeApproval && pendingApprovalActions.length > 0
      ? actions.filter((action) => action.id === activeApproval.id)
      : actions;

  return (
    <div className="mb-4 w-full overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900 shadow-lg">
      <div className="divide-y divide-zinc-800/50">
        {visibleActions.map((action) => {
          const index = actions.indexOf(action);
          const isSuccess = action.status === 'success';
          const isFailed = action.status === 'failed';
          const isRunning = action.status === 'running';
          const isExpanded = expandedIndex === index;

          let statusIcon;
          let statusTextColor;

          if (isRunning) {
            if (action.approvalNeeded) {
              statusIcon = (
                <Hourglass className="h-5 w-5 animate-pulse text-amber-400" />
              );
              statusTextColor = 'text-amber-300';
            } else if (action.disapprovedByUser) {
              // The user already declined/gave feedback on this specific
              // item (synchronous, instant). The backend hasn't confirmed
              // yet -- status stays "running" until the whole batch resolves
              // and resubmits together -- but showing the generic spinner
              // here would misleadingly suggest this item is still awaiting
              // input, when it's actually just waiting on its siblings.
              statusIcon = <XCircle className="h-5 w-5 text-red-400" />;
              statusTextColor = 'text-red-300';
            } else {
              const phase = action.running_phase || 'calling_tool';
              switch (phase) {
                case 'filtering':
                  statusIcon = (
                    <Filter className="h-5 w-5 animate-pulse text-indigo-300" />
                  );
                  statusTextColor = 'text-cyan-300';
                  break;
                case 'analyzing':
                  statusIcon = (
                    <BrainCircuit className="h-5 w-5 animate-analyzing-glow text-indigo-400" />
                  );
                  statusTextColor = 'text-indigo-300';
                  break;
                case 'calling_tool':
                default:
                  statusIcon = (
                    <img
                      alt="Logo"
                      src="/assets/logo.png"
                      className="w-6 animate-rotatePause"
                    />
                  );
                  statusTextColor = 'text-blue-300';
                  break;
              }
            }
          } else if (isSuccess) {
            statusIcon = <CheckCircle className="h-5 w-5 text-emerald-400" />;
            statusTextColor = 'text-zinc-300';
          } else if (isFailed) {
            statusIcon = <XCircle className="h-5 w-5 text-red-400" />;
            statusTextColor = 'text-red-300';
          } else {
            // pending / queued / unknown status
            statusIcon = <Hourglass className="h-5 w-5 text-zinc-500" />;
            statusTextColor = 'text-zinc-400';
          }

          const isActiveApproval =
            !!activeApproval && activeApproval.id === action.id;

          return (
            <React.Fragment key={action.id || index}>
              {/* ── Tool row ── */}
              {/* eslint-disable-next-line */}
              <div
                data-tool-id={action.id}
                className={`cursor-pointer transition-colors duration-300 ${
                  isActiveApproval
                    ? 'bg-amber-500/10'
                    : isExpanded
                      ? 'bg-zinc-800/40'
                      : 'hover:bg-zinc-800/40'
                }`}
                onClick={() => handleToggle(index)}
              >
                <div className="relative flex items-start gap-3 px-4 py-2 transition-colors duration-200 sm:items-center">
                  <div className="mb-1 mt-1 flex-shrink-0">{statusIcon}</div>

                  <div className="flex min-w-0 flex-1 flex-col gap-x-4 gap-y-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <p
                        className={`truncate text-xs font-medium ${statusTextColor}`}
                      >
                        {action.action_description}
                      </p>
                    </div>
                  </div>

                  {isRunning &&
                    !action.approvalNeeded &&
                    !action.disapprovedByUser && (
                      <div className="absolute bottom-0 left-0 right-0 h-1 overflow-hidden bg-zinc-700/50">
                        <div
                          className="h-full origin-left bg-primary_c"
                          style={{
                            animation: 'fillProgress 30s ease-out forwards',
                          }}
                        />
                      </div>
                    )}
                </div>

                <div
                  className="overflow-hidden transition-all duration-300 ease-in-out"
                  style={{ maxHeight: isExpanded ? '500px' : '0' }}
                >
                  <ArgumentsDisplay
                    args={action.args}
                    args_streaming={action.args_streaming}
                  />
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* ── Single shared approval card -- one pending item at a time, in
          order, instead of a full card stacked under every row. ── */}
      {activeApproval && onApprove && onDecline && (
        <div className="border-t border-zinc-800/50">
          {totalInBatch > 1 && (
            <div className="flex items-center justify-between px-4 pb-2 pt-2.5">
              <span className="text-xs font-medium text-amber-300">
                Review required
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={goToPrevApproval}
                  disabled={!canGoPrevApproval}
                  title="Previous pending item"
                  className="flex h-6 w-6 items-center justify-center rounded-md text-zinc-500 transition-colors hover:bg-zinc-800 hover:text-zinc-200 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="min-w-[52px] text-center text-xs text-zinc-500">
                  {activeBatchPosition} of {totalInBatch}
                </span>
                <button
                  onClick={goToNextApproval}
                  disabled={!canGoNextApproval}
                  title="Next pending item"
                  className="flex h-6 w-6 items-center justify-center rounded-md text-zinc-500 transition-colors hover:bg-zinc-800 hover:text-zinc-200 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
          <InlineApprovalCard
            key={activeApproval.id}
            toolCall={approvalItemMap.get(activeApproval.id)}
            onApprove={onApprove}
            onDecline={onDecline}
            onAttachFile={onAttachFile}
            taskId={taskId}
          />
        </div>
      )}

      <style>{`
        @keyframes fillProgress {
          0%   { transform: scaleX(0); }
          10%  { transform: scaleX(0.4); }
          20%  { transform: scaleX(0.6); }
          30%  { transform: scaleX(0.7); }
          40%  { transform: scaleX(0.75); }
          50%  { transform: scaleX(0.8); }
          60%  { transform: scaleX(0.83); }
          70%  { transform: scaleX(0.86); }
          80%  { transform: scaleX(0.88); }
          90%  { transform: scaleX(0.9); }
          95%  { transform: scaleX(0.91); }
          97%  { transform: scaleX(0.92); }
          99%  { transform: scaleX(0.93); }
          100% { transform: scaleX(0.94); }
        }
        .custom-scrollbar::-webkit-scrollbar { width: 8px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: #3f3f46;
          border-radius: 10px;
          border: 2px solid transparent;
          background-clip: content-box;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background-color: #52525b; }
        .custom-scrollbar { scrollbar-width: thin; scrollbar-color: #3f3f46 transparent; }
      `}</style>
    </div>
  );
};

export default ActionTrackerEmbedded;
